import React, { useContext } from "react"
import { Button, Col, Form, Row } from "react-bootstrap"
import { useDispatch, useSelector } from "react-redux"
import { Formik, useField } from "formik"
import { mixed, object, string } from "yup"

import FormikBootstrap from "@/components/formik-bootstrap"
import { removeFieldListItem, updateFieldListItem } from "@/onboarding/features/product/Slice"
import SubmitButton from "@/onboarding/features/product/components/shared/buttons/SubmitButton"
import SubmitAndApplyButton from "@/onboarding/features/product/components/shared/buttons/SubmitAndApplyButton"
import { SubmittingContext } from "@/onboarding/features/product/helpers"
import SubmittingSpinner from "@/onboarding/features/product/components/shared/SubmittingSpinner"
import { getAcceptedMediaFormats } from "@/onboarding/features/product/selectors"
import { isBlank } from "@/utils"

const FormikFileInput = ({ label, ...props }) => {
  const [field, meta] = useField(props)
  const showError = meta.touched && meta.error
  const input = <FormikBootstrap.FileInput name="imageSrc" label="File" />
}

const IMAGE_FORMAT_LABELS: Record<string, string> = {
  "image/jpeg": "JPG (maximum quality)",
  "image/png": "PNG",
  "image/webp": "WebP",
}

const formatImageFormats = (formats: string[]): string => {
  const labels = formats.filter((f) => f in IMAGE_FORMAT_LABELS).map((f) => IMAGE_FORMAT_LABELS[f])

  return labels.length > 1
    ? `${labels.slice(0, -1).join(", ")}, or ${labels.at(-1)}`
    : (labels[0] ?? "")
}

const Edit = ({ media }): JSX.Element => {
  const dispatch = useDispatch()
  const [submitting, setSubmitting] = useContext(SubmittingContext)
  const acceptedFormats = useSelector(getAcceptedMediaFormats)
  const hasExistingImage = !isBlank(media.componentAttributes.imageSrc)

  const validationSchema = object({
    name: string().required(),
    description: string().required(),
    imageSrc: mixed()
      .test("file-type", "Unsupported file format. ", (valueObject: any) => {
        const value = valueObject?.value
        if (!value) return true
        return (acceptedFormats ?? []).includes(value.type)
      })
      .nullable()
      .test(
        "image-required",
        "An image is required.",
        (valueObject: any) => hasExistingImage || Boolean(valueObject?.value)
      ),
  })

  return (
    <Formik
      initialValues={{
        ...media.componentAttributes,
        imageSrc: null,
      }}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        setSubmitting(true)
        dispatch(
          updateFieldListItem({
            fieldName: media.fieldName,
            index: media.componentIndex,
            value: { ...values, imageSrc: values.imageSrc ?? media.componentAttributes.imageSrc },
          })
        )
      }}
    >
      {({ isValid, handleSubmit }) => (
        <Form className="d-flex flex-column gap-4" onSubmit={handleSubmit}>
          <Row>
            <Col xs={6}>
              <FormikBootstrap.TextInput name="name" label="Name" />
            </Col>
            <Col xs={6}>
              <FormikBootstrap.Select name="promotional" label="Promotional Use">
                <option value="false">Not allowed</option>
                <option value="true">Allowed</option>
              </FormikBootstrap.Select>
            </Col>
          </Row>
          <Row>
            <Col>
              <FormikBootstrap.TextAreaInput name="description" label="Description" />
            </Col>
          </Row>
          <Row>
            <Col>
              <FormikBootstrap.FileInput name="imageSrc" label="File" />
            </Col>
          </Row>
          <Row>
            <Col>
              <b>Photo Specifications</b>
              <ul>
                <li>Minimum width: 1920px</li>
                <li>Minimum height: 1080px</li>
                <li>Orientation: Landscape</li>
                <li>Resolution: High (minimum 300 pixels per inch)</li>
                <li>Format: {formatImageFormats(acceptedFormats ?? [])}</li>
              </ul>
              <b>Video Specifications</b>
              <ul>
                <li>Resolution: 1920x1080</li>
                <li>Format: MP4</li>
                <li>
                  Web version: voice over and graphics limited to tour description. No mention of
                  third-party company/operator brand. We prefer a Youtube video as we would need
                  that format to be able to upload it.
                </li>
                <li>
                  Onboard version: No graphics on the bottom third of screen so we can insert our
                  own tour name, code, etc.
                </li>
                <li>
                  Note: Do not include/mention tour operator brand on either version if they&apos;re
                  different.
                </li>
              </ul>
            </Col>
          </Row>
          <Row>
            <Col className="d-flex gap-2">
              <SubmitButton />
              <SubmitAndApplyButton />
              <Button
                className="text-nowrap"
                variant="danger"
                disabled={submitting || !isValid}
                onClick={() => {
                  setSubmitting(true)
                  dispatch(
                    removeFieldListItem({
                      fieldName: media.fieldName,
                      index: media.componentIndex,
                    })
                  )
                }}
              >
                <i className="fas fa-trash me-2" />
                Delete
              </Button>
              <SubmittingSpinner />
            </Col>
          </Row>
        </Form>
      )}
    </Formik>
  )
}

export default Edit
